import { useEditMarket } from '../../provider/edit-market/EditMarket.hook.ts';
import { lsLoad, lsSave } from '../../tools/Storage';
import StorageEnum from '../../enums/Storage.enum';
import { toast } from 'react-toastify';
import { toastOptions } from '../../tools/helpers/ToastHelper';
import type { MarketComponentMarketComponentIO } from '../../generated/fresh-guard-api';
import { useTranslation } from 'react-i18next';
import { useMarketComponentUpdateOptimistic } from '../../apis/market-component/useMarketComponentUpdateOptimistic.ts';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../apis/query-keys.ts';

export const useEditMarketController = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const {
    setEnableZoom,
    setEnableDragAndDropMarketArea,
    setEnableDragAndDropToolbar,
    marketComponentsEdited,
    clearIdsOfMarketComponents,
    initMarketComponents,
    resetMarketComponentsEdited,
    isInEditMode,
    setIsInEditMode,
  } = useEditMarket();

  const { mutate } = useMarketComponentUpdateOptimistic();

  const startEditMarket = () => {
    setIsInEditMode(true);
    setEnableZoom(false);
    setEnableDragAndDropMarketArea(true);
    setEnableDragAndDropToolbar(true);
  };

  const finishEditMarket = () => {
    setEnableZoom(true);
    setEnableDragAndDropMarketArea(false);
    setEnableDragAndDropToolbar(false);
    clearIdsOfMarketComponents();
    resetMarketComponentsEdited();

    if (marketComponentsEdited && marketComponentsEdited.length > 0) {
      mutate(
        {
          marketComponentMarketComponentCollectionIO: {
            marketComponentIOs: marketComponentsEdited,
          },
        },
        {
          onSuccess: async (data) => {
            lsSave(StorageEnum.MarketComponents, data);
            initMarketComponents(data as MarketComponentMarketComponentIO[]);

            const market = lsLoad(StorageEnum.Market);
            const marketId = market?.id?.toString();
            await queryClient.invalidateQueries({
              queryKey: queryKeys.marketComponents.byMarket(marketId),
            });

            setIsInEditMode(false);
            toast.success(t('success.marketComponentsSaved'), toastOptions);
          },
          onError: () => {
            setIsInEditMode(false);
          },
        }
      );
    } else {
      setIsInEditMode(false);
    }
  };

  return {
    isInEditMode,
    startEditMarket,
    finishEditMarket,
  };
};
